import { InfoCircleOutlined, InteractionOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, message, Modal, ModalProps, Pagination, Popover, Space } from 'antd';
import { useTranslate } from '@tolgee/react';
import { batchUpdateAccount, NeedToUpdateStore } from 'services/api/admin';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import UpdateAccountForm, { AccountFormValue } from './Form';
import { useTransAppProductName } from 'hooks/useTrans';

const ModalContent = styled.div`
  overflow: auto;
`;
const TotalTip = styled(Space)`
  margin-bottom: 12px;
`;

interface Props extends ModalProps {
  list: NeedToUpdateStore[];
  refresh: () => void;
  mode: 'single' | 'multiple';
}

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  button {
    color: #336fff;
  }
`;
const Ul = styled.ul`
  padding: 0px 16px;
  max-width: 340px;
  margin: 0;
`;

const UpdateAccountModal = (props: Props) => {
  const { list: propsList, refresh, mode, ...modalProps } = props;
  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });
  const allFormValueRef = useRef<Map<string, AccountFormValue>>(new Map());
  const [disabledBatchUpdateBtn, setDisabledBatchUpdateBtn] = useState(true);
  const { list, offset } = useMemo(() => {
    const { page, pageSize } = pagination;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    allFormValueRef.current.clear();
    setDisabledBatchUpdateBtn(true);
    return { list: propsList.slice(start, end), offset: start };
  }, [propsList, pagination]);
  const { run: onSubmit, loading } = useRequest(
    async (accounts: AccountFormValue[]) => {
      try {
        await batchUpdateAccount(accounts);
        message.success(t('ViewShops.更新成功'));
        refresh();
        setPagination((pre) => ({ ...pre, page: 1 }));
      } catch (error) {
        message.error(t('ViewShops.更新失败'));
      }
    },
    { manual: true },
  );
  const passwordValidator = useCallback(
    (value: string) => {
      if (value.length < 8) {
        return t('ViewShops.密码不能少于8位');
      }
      if (/[\u4e00-\u9fa5]+/.test(value)) {
        return t('ViewShops.密码中不能有汉字');
      }
      return undefined;
    },
    [t],
  );

  const onOneFormChange = useCallback(
    (values: AccountFormValue) => {
      const { storeId } = values;
      allFormValueRef.current.set(storeId, values);
      const canSubmit = [...allFormValueRef.current.values()].some(
        (item) =>
          item.account && item.password && item.storeId && !passwordValidator(item.password),
      );
      setDisabledBatchUpdateBtn(!canSubmit);
    },
    [passwordValidator],
  );
  return (
    <Modal
      width={800}
      destroyOnClose
      title={
        <Space>
          {t('ViewShops.更新帐号密码')}
          <Popover
            getPopupContainer={(node) => node}
            placement="bottom"
            content={
              <Ul>
                <li>{appProductNameT('ViewShops.storagePwdWarningMessage')}</li>
                <li>{appProductNameT('ViewShops.autoAuthFixWarningMessage')}</li>
              </Ul>
            }
          >
            <InfoCircleOutlined />
          </Popover>
        </Space>
      }
      footer={
        mode === 'multiple' && (
          <Footer>
            <Button
              type="text"
              disabled={disabledBatchUpdateBtn}
              onClick={() => {
                onSubmit([...allFormValueRef.current.values()]);
              }}
            >
              {t('ViewShops.本页全部更新')}
            </Button>
            <Pagination
              total={propsList.length}
              onChange={(page, pageSize) => setPagination({ page, pageSize })}
              current={pagination.page}
              pageSize={pagination.pageSize}
              pageSizeOptions={['5', '10', '20', '50', '100']}
            />
          </Footer>
        )
      }
      {...modalProps}
    >
      <ModalContent>
        {mode === 'multiple' && (
          <TotalTip>
            <InteractionOutlined />
            <span>{t('ViewShops.待更新x家店铺', { num: propsList.length })}</span>
          </TotalTip>
        )}

        {list.map((item, index) => (
          <UpdateAccountForm
            item={item}
            index={index + offset}
            onChange={onOneFormChange}
            loading={loading}
            key={item.storeId}
            passwordValidator={passwordValidator}
            onSubmit={(values) => {
              onSubmit([values]);
            }}
          />
        ))}
      </ModalContent>
    </Modal>
  );
};

export default UpdateAccountModal;
